import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import BASE_URL from "../../config/config";
import PriceDisplay from "../common/PriceDisplay";
import Loader from "../common/Loader";
import ErrorMessage from "../common/ErrorMessage";
import CoursePurchase from "./CoursePurchase";
import { useAuth } from "../Auth/AuthContext";
import "../../css/CourseDetail.css";
import CommentsSection from "./CommentsSection";
import {
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaUsers,
  FaStar,
  FaChartBar,
  FaTimes,
} from "react-icons/fa";
import PlayButton from "../template/PlayButton";


const CourseDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const { user } = useAuth();
  const [purchaseStatus, setPurchaseStatus] = useState(null);
  const [sessions, setSessions] = useState([]);
  const token = localStorage.getItem("token");
  const [sessionsError, setSessionsError] = useState(null);
  const [openSessions, setOpenSessions] = useState({});
  const [showCourseVideo, setShowCourseVideo] = useState(false);
  const courseVideoRef = useRef(null);
  const [playingVideos, setPlayingVideos] = useState({});

  const getLevelText = (level) => {
    switch (level) {
      case "beginner":
        return "مقدماتی";
      case "intermediate":
        return "متوسط";
      case "advanced":
        return "پیشرفته";
      default:
        return "نامشخص";
    }
  };

  // دریافت اطلاعات دوره
  useEffect(() => {
    const fetchCourse = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${BASE_URL}/public/courses/${id}`);
        if (!response.ok) throw new Error("دوره یافت نشد");
        const data = await response.json();
        setCourse(data.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [id]);

  // دریافت جلسات (فقط برای عضو)
  useEffect(() => {
    const fetchSessions = async () => {
     
      // برای عموم مردم
      // if (!user) {
      //   setSessions([]);
      //   setSessionsError("برای مشاهده سرفصل‌ها باید وارد شوید.");
      //   return;
      // }

      try {
        const response = await fetch(
          `${BASE_URL}/courses/${id}/sessions-info`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.message || "خطا در دریافت جلسات");
        }

        const data = await response.json();
        setSessions(data.data || []);
        setSessionsError(null);
      } catch (err) {
        setSessions([]);
        setSessionsError(err.message);
      }
    };

    fetchSessions();
  }, [id, token]); // user برای فقط اعضا

  // بررسی وضعیت خرید
  useEffect(() => {
    const checkPurchaseStatus = async () => {
      if (!user) return;
      try {
        const response = await fetch(`${BASE_URL}/user/courses/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        setPurchaseStatus(data);
      } catch (e) {
        setPurchaseStatus({ status: "error", message: "خطای ارتباط با سرور" });
      }
    };

    if (id && user) checkPurchaseStatus();
  }, [user, id, token]);

  // تابع پخش ویدیوی معرفی
// تابع پخش ویدیوی معرفی
const renderCourseMedia = (course) => {

  const hasIntroVideo =
    course.intro_video &&
    course.intro_video.endsWith(".mp4");

  const mediaUrl = `${BASE_URL}/get-media?file_id=${encodeURIComponent(
    course.intro_video
  )}&type=video`;

  const handlePlayClick = () => {
    setShowCourseVideo(true);
    setTimeout(() => {
      if (courseVideoRef.current) {
        courseVideoRef.current.play().catch(() => {});
      }
    }, 100);
  };

  // 1️⃣ اگر ویدیو معرفی نیست → همیشه کاور دوره را نمایش بده
  if (!hasIntroVideo) {

    return (
      <div
        className="mb-8 rounded-2xl overflow-hidden shadow-2xl"
        style={{ aspectRatio: "16/9" }}
      >
        <div
          className="w-full h-full bg-gray-900"
          style={{
            backgroundImage: `url(${course.cover_pic || "/images/course-default.jpg"})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      </div>
    );
  }

  // 2️⃣ اگر ویدیو معرفی هست
  return (
    <div
      className="mb-8 rounded-2xl overflow-hidden shadow-2xl"
      style={{ aspectRatio: "16/9" }}
    >
      {!showCourseVideo ? (
        <div
          className="w-full h-full bg-gray-900 flex items-center justify-center cursor-pointer relative"
          onClick={handlePlayClick}
          style={{
            backgroundImage: `url(${course.cover_pic || "/images/course-default.jpg"})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="bg-black bg-opacity-50 w-full h-full flex items-center justify-center">
            <PlayButton />
          </div>
        </div>
      ) : (
        <video
          ref={courseVideoRef}
          className="w-full h-full"
          controls
          autoPlay
          poster={course.cover_pic}
          src={mediaUrl}
        />
      )}
    </div>
  );
};

  // تابع پخش ویدیوهای جلسات
  const renderSessionVideo = (session, course) => {
    const file = session.file_url?.trim();

    const hasVideo =
      file &&
      file !== "null" &&
      file !== "undefined" &&
      file !== "0" &&
      file !== "-" &&
      !file.startsWith("temp_") &&
      file.endsWith(".mp4");


    // اگر ویدیو وجود ندارد → نمایش کاور
    if (!hasVideo) {
      
      return (
        <div
          className="mt-4 w-full rounded-lg overflow-hidden"
          style={{
            aspectRatio: "16/9",
            backgroundImage: `url(${session.thumbnail || course.cover_pic || "/images/course-default.jpg"})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      );
    }

    // اگر ویدیو وجود دارد → نمایش پلیر
    const mediaUrl = `${BASE_URL}/get-media?file_id=${encodeURIComponent(session.file_url)}&type=video`;
    const isPlaying = playingVideos[session.id];

    const handlePlayClick = () => {
      setPlayingVideos((prev) => ({ ...prev, [session.id]: true }));
    };

    const handleCloseVideo = (e) => {
      e.stopPropagation();
      setPlayingVideos((prev) => ({ ...prev, [session.id]: false }));
    };
    
    return (
      <div className="mt-4">
        {!isPlaying ? (
          <div
            className="w-full h-48 bg-gray-900 rounded-lg flex items-center justify-center cursor-pointer relative"
            onClick={handlePlayClick}
            style={{
              backgroundImage: `url(${session.thumbnail || course.cover_pic || "/images/course-default.jpg"})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="bg-black bg-opacity-50 w-full h-full flex items-center justify-center rounded-lg">
              <PlayButton />
            </div>
          </div>
        ) : (
          <div className="relative rounded-lg overflow-hidden" style={{ aspectRatio: "16/9" }}>
            <button
              onClick={handleCloseVideo}
              className="absolute top-2 right-2 bg-black bg-opacity-50 text-white rounded-full p-1 z-10 hover:bg-opacity-70"
            >
              <FaTimes />
            </button>
            <video
              className="w-full h-full"
              controls
              autoPlay
              poster={session.thumbnail || course.cover_pic || "/images/course-default.jpg"}
            >
              <source src={mediaUrl} type="video/mp4" />
              مرورگر شما از پخش ویدیو پشتیبانی نمی‌کند.
            </video>
          </div>
        )}
      </div>
    );
  };


  const toggleSession = (sessionId) => {
    setOpenSessions((prev) => ({
      ...prev,
      [sessionId]: !prev[sessionId],
    }));
  };

  const handlePurchaseClick = () => {
    if (!user) {
      navigate("/login");
      return;
    }
    setShowPurchaseModal(true);
  };

  const previewCourse = () => {
    navigate(`/dashboard/courses/${id}`);
  };

  if (loading) return <Loader />;
  if (error) return <ErrorMessage message={error} />;
  if (!course) return <div>دوره یافت نشد.</div>;

  let actionButton;
  if (!user) {
    actionButton = (
      <button
        className="w-full btn-primary mb-3 px-8 mt-2 py-4 rounded-xl font-bold text-lg transition-all shadow-lg"
        onClick={handlePurchaseClick}
      >
        🚀 ثبت‌نام در دوره
      </button>
    );
  } else if (purchaseStatus?.status === "success") {
    actionButton = (
      <button className="w-full btn-primary mb-3 px-8 mt-2 py-4 rounded-xl font-bold text-lg transition-all shadow-lg">
        <Link to={`/dashboard/courses/${course.id}`} className="">
          📚 شروع یادگیری
        </Link>
      </button>
    );
  } else if (purchaseStatus?.status === "pending") {
    actionButton = (
      <button
        className="w-full btn-primary mb-3 px-8 mt-2 py-4 rounded-xl font-bold text-lg transition-all shadow-lg"
        disabled
      >
        در انتظار تایید
      </button>
    );
  } else if (purchaseStatus?.status === "available") {
    actionButton = (
      <button
        className="w-full mb-3 mt-2 btn-primary px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg"
        onClick={handlePurchaseClick}
      >
        🚀 ثبت‌نام در دوره
      </button>
    );
  }

  // محاسبه قیمت نهایی با تخفیف
  const calculateFinalPrice = (price, discount) => {
    const originalPrice = parseFloat(price) || 0;
    const discountPercent = parseFloat(discount) || 0;
    return originalPrice - (originalPrice * discountPercent) / 100;
  };


  return (
    <div className="main-content-wrapper">
      {/* هدر دوره */}
      <section className="course-hero py-20 from-primary to-dark">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {renderCourseMedia(course)}

              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                {course.title}
              </h1>

              <p className="text-xl text-white opacity-95 mb-8 leading-relaxed">
                {course.short_description ||
                  "آموزش کامل با پروژه‌های عملی و تمرین‌های کاربردی"}
              </p>

              <div className="flex flex-wrap gap-6 text-white mb-8">
                <div className="flex items-center gap-2">
                  <FaClock /> <span>{course.duration}</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaChartBar /> <span>سطح: {getLevelText(course.level)}</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="course-info-card bg-white rounded-2xl p-6 shadow-xl">
                <div className="text-center mb-6">
                  {course.is_free || course.price === 0 ? (
                    <div className="course-price free">
                      <span className="text-3xl font-bold text-green-600">
                        رایگان
                      </span>
                    </div>
                  ) : (
                    <div className="course-price-container">
                      {parseFloat(course.discount) > 0 ? (
                        <>
                          <div className="discount-badge-large">
                            {course.discount}% تخفیف
                          </div>
                          <div className="original-price-detail">
                            {parseFloat(course.price).toLocaleString()} تومان
                          </div>
                          <div className="final-price-detail">
                            {calculateFinalPrice(
                              course.price,
                              course.discount
                            ).toLocaleString()}{" "}
                            تومان
                          </div>
                        </>
                      ) : (
                        <div className="regular-price-detail">
                          {parseFloat(course.price).toLocaleString()} تومان
                        </div>
                      )}
                    </div>
                  )}
                  <p className="text-gray-600 mt-3">دسترسی کامل به دوره</p>
                </div>

                <ul className="course-features mb-8 space-y-3">
                  <li className="flex items-center gap-3">
                    🎥 <span>ویدیوهای آموزشی</span>
                  </li>
                  <li className="flex items-center gap-3">
                    📝 <span>تمرین عملی</span>
                  </li>
                  <li className="flex items-center gap-3">
                    📱 <span>دسترسی موبایل</span>
                  </li>
                  <li className="flex items-center gap-3">
                    💬 <span>پشتیبانی ۲۴/۷</span>
                  </li>
                  <li className="flex items-center gap-3">
                    ♾️ <span>دسترسی مادام‌العمر</span>
                  </li>
                </ul>

                {actionButton}

                <button
                  className="w-full border-2 border-primary text-primary px-8 py-4 rounded-xl font-bold text-lg hover:bg-primary hover:text-white transition-all"
                  onClick={previewCourse}
                >
                  🎬 مشاهده جلسات
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* محتوای دوره */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="mb-12">
              <h2 className="text-3xl font-bold gradient-text mb-6">
                درباره این دوره
              </h2>
              <div
                className="prose prose-lg"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    course.description || "توضیحاتی موجود نیست."
                  ),
                }}
              ></div>
            </div>

            {/* نمایش سرفصل‌ها فقط برای کاربران عضو */}
            {user || true ? (
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-blue-800 mb-6">
                  سرفصل‌های دوره
                </h2>

                {sessions.length === 0 ? (
                  <p className="text-gray-500">
                    هیچ جلسه‌ای برای نمایش وجود ندارد.
                  </p>
                ) : (
                  <div className="space-y-4">

                    {sessions.map((session) => (

                      <div
                        key={session.id}
                        className="border border-gray-200 rounded-xl overflow-hidden"
                      >
                        {/* هدر آکاردئون */}
                        <div
                          className="flex justify-between items-center p-4 bg-white cursor-pointer hover:bg-blue-50 transition-colors duration-200"
                          onClick={() => toggleSession(session.id)}
                        >
                          <span className="font-medium text-blue-900">
                            {session.title}
                          </span>
                          <span
                            className={`transform transition-transform duration-300 ${openSessions[session.id] ? "rotate-180" : ""
                              }`}
                          >
                            <FaChevronDown className="text-blue-700" />
                          </span>
                        </div>

                        {/* محتوای آکاردئون */}
                        <div
                          className={`transition-max-height duration-300 ease-in-out overflow-auto ${openSessions[session.id] ? "max-h-96" : "max-h-0"
                            }`}
                        >
                          <div className="p-4 bg-gray-50 border-t border-gray-100">
                            <p
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(
                                  session.description || ""
                                ),
                              }}
                              className="text-gray-700"
                            ></p>
                            <div className="flex justify-between items-center text-sm mt-3">
                              <span className="text-gray-500">
                                مدت: {session.duration || "نامشخص"} دقیقه
                              </span>
                            </div>

                            {/* نمایش ویدیو جلسه */}
                            {renderSessionVideo(session, course)}

                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="mb-12 p-6 bg-white rounded-xl shadow text-center">
                <p className="text-gray-700 text-lg">
                  برای مشاهده سرفصل‌های دوره وارد شوید.
                </p>
                <button
                  onClick={() => navigate("/login")}
                  className="mt-4 bg-blue-800 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
                >
                  ورود به حساب
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* نظرات */}
      <CommentsSection courseId={course.id} />

      {/* CTA */}
      <section className="hero-bg py-20 bg-gradient-to-br from-primary to-dark text-center">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            آماده شروع یادگیری هستید؟
          </h2>
          <p className="text-white text-xl mb-12 opacity-95">
            همین الان در دوره ثبت‌نام کنید و مسیر یادگیری خود را آغاز کنید
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center align-center">
            <button
              className="mb-3 btn-primary px-5 py-4 rounded-xl font-bold text-xl transition-all shadow-lg"
              onClick={handlePurchaseClick}
            >
              🚀 ثبت‌نام در دوره
            </button>
            <button
              className="btn-secondary text-white px-5 py-4 rounded-xl font-bold text-xl transition-all shadow-xl"
              onClick={() => navigate("/courses")}
            >
              📚 مشاهده دوره‌های بیشتر
            </button>
          </div>
        </div>
      </section>

      {/* مودال خرید */}
      {showPurchaseModal && (
        <CoursePurchase
          courseId={course.id}
          onClose={() => setShowPurchaseModal(false)}
          isOpen={showPurchaseModal}
        />
      )}
    </div>
  );
};

export default CourseDetail;
